import { Document, Page, Text, View } from "@react-pdf/renderer";
import { calculateClearPatientFee } from "helpers/fee";
import { centsToDollars } from "helpers/price";
import { parseBoldSegments } from "helpers/text";
import { useMemo } from "react";

import { ChargeDescriptionV3 } from "./partials/ChargeDescriptionV3";
import { Footer } from "./partials/Footer";
import { Header } from "./partials/Header";
import { PatientInfoCustom } from "./partials/PatientInfoCustom";
import { PatientInfoDefault } from "./partials/PatientInfoDefault";
import { PlasticSurgeryText } from "./partials/PlasticSurgeryText";
import { ProcedureBreakdown } from "./partials/ProcedureBreakdown";
import { V3Disclaimer } from "./partials/V3Disclaimer";
import { styles } from "./styles";

export function CustomProviderPdf({
  facilityLogo,
  fullAddress,
  phoneNumber,
  facilityName,
  clearPatientFeeValueInPercentage,
  name,
  dateOfBirth,
  today,
  expirationDate,
  uniqueProcedureListByProviders,
  uniqueProcedureList = [],
  payerName = "",
  serviceDate = "",
  isGFE = false,
  accountNumber,
  note,
  showPromptPayDiscount,
  isVersion3Pdf,
  providerWithCustomPdf,
  hoursEstimated = null,
  surgeonFees = 0,
  anesthesiaFees = 0,
  facilityFees = 0,
  scd = 0,
  exparel = 0,
  clearPriceToPay = 0,
}: {
  facilityLogo: string;
  fullAddress?: string;
  phoneNumber?: string;
  facilityName?: string;
  clearPatientFeeValueInPercentage?: number | null;
  name: string;
  dateOfBirth: string;
  today: string;
  expirationDate: string;
  uniqueProcedureListByProviders: any[];
  uniqueProcedureList?: any[];
  payerName?: string;
  serviceDate?: string;
  isGFE?: boolean;
  accountNumber?: string;
  note?: string;
  showPromptPayDiscount?: boolean;
  isVersion3Pdf?: boolean;
  providerWithCustomPdf?: any;
  hoursEstimated?: number | null;
  surgeonFees?: number;
  anesthesiaFees?: number;
  facilityFees?: number;
  scd?: number;
  exparel?: number;
  clearPriceToPay?: number;
}) {
  const customPdfContent = providerWithCustomPdf?.customPdfContent;
  const summaryLayout = customPdfContent?.summaryLayout;
  const feeTableLayout = customPdfContent?.feeTableLayout;
  const isChargeDescription = feeTableLayout === "chargeDescription";
  const introductionText = customPdfContent?.introductionText;
  const introductionSegments = introductionText
    ? parseBoldSegments(introductionText)
    : [];
  const isCustomPatientInfo = summaryLayout === "custom";

  const showConvenienceFeesSeparately = useMemo(() => {
    return (
      isChargeDescription &&
      customPdfContent?.convenienceServiceFees === "showSeparately"
    );
  }, [isChargeDescription, customPdfContent]);

  const clearPatientFee = useMemo(() => {
    const subtotal = uniqueProcedureListByProviders.reduce(
      (acc: any, proceduresByProvider: any) => {
        return acc + proceduresByProvider.subtotal;
      },
      0
    );
    // Calculate fee with rounding (subtotal is in cents, need dollars for fee calculation)
    // Return fee in cents
    return clearPatientFeeValueInPercentage
      ? calculateClearPatientFee(
          centsToDollars(subtotal),
          clearPatientFeeValueInPercentage
        ) * 100
      : 0;
    // OLD LOGIC (exact percentage, no rounding):
    // return clearPatientFeeValueInPercentage
    //   ? subtotal * (clearPatientFeeValueInPercentage / 100)
    //   : 0;
  }, [uniqueProcedureListByProviders, clearPatientFeeValueInPercentage]);

  return (
    <Document>
      <Page size="LETTER" style={{ ...styles.page, position: "relative" }}>
        <Header
          facilityLogo={facilityLogo}
          fullAddress={fullAddress}
          phoneNumber={phoneNumber}
          showAddress={false}
        />
        {/* CONTENT */}
        <View style={styles.content} wrap>
          <View style={{ ...styles.section, marginTop: 0 }}>
            <Text
              style={{
                ...styles.title,
                ...{ color: "#234990", fontSize: "13px" },
              }}
            >
              {customPdfContent?.documentTitle}
            </Text>
          </View>
          <View style={{ ...styles.section, marginTop: 0 }}>
            {introductionSegments.length > 0 && (
              <Text style={{ ...styles.bodyText, lineHeight: 1.2 }}>
                {introductionSegments.map((segment, index) =>
                  segment.bold ? (
                    <Text key={index} style={{ fontFamily: "Helvetica-Bold" }}>
                      {segment.text}
                    </Text>
                  ) : (
                    <Text key={index}>{segment.text}</Text>
                  )
                )}
              </Text>
            )}
          </View>
          {isCustomPatientInfo ? (
            <PatientInfoCustom
              name={name}
              dateOfBirth={dateOfBirth}
              today={today}
              expirationDate={expirationDate}
            />
          ) : (
            <PatientInfoDefault
              name={name}
              dateOfBirth={dateOfBirth}
              payerName={payerName}
              serviceDate={serviceDate}
              isGFE={isGFE}
              accountNumber={accountNumber}
              uniqueProcedureList={uniqueProcedureList}
              style={{ marginTop: 2, marginBottom: 10 }}
            />
          )}
          <View style={{ ...styles.section, marginBottom: 4, marginTop: 0 }}>
            {isChargeDescription ? (
              <ChargeDescriptionV3
                surgeonFees={
                  showConvenienceFeesSeparately
                    ? surgeonFees
                    : surgeonFees + clearPatientFee
                }
                anesthesiaFees={anesthesiaFees}
                facilityFees={facilityFees}
                scd={scd}
                exparel={exparel}
                clearPriceToPay={clearPriceToPay}
                proceduresByProviders={uniqueProcedureListByProviders}
                clearPatientFeeValueInPercentage={
                  showConvenienceFeesSeparately
                    ? clearPatientFeeValueInPercentage
                    : null
                }
              />
            ) : (
              <View style={styles.list}>
                <ProcedureBreakdown
                  proceduresByProviders={uniqueProcedureListByProviders}
                  clearPatientFeeValueInPercentage={
                    clearPatientFeeValueInPercentage
                  }
                />
              </View>
            )}
          </View>
          {!customPdfContent?.showTermsAndConditions && (
            <View style={{ flexGrow: 1 }} />
          )}
          <View wrap={false}>
            {!showPromptPayDiscount && !isVersion3Pdf && (
              <View style={styles.content}>
                <View style={{ ...styles.section, marginTop: 5 }}>
                  <Text
                    style={{
                      ...styles.bodyText,
                      lineHeight: 1.2,
                      textAlign: "justify",
                    }}
                  >
                    {note || ""}
                  </Text>
                </View>
              </View>
            )}
            {isVersion3Pdf && (
              <View
                style={{
                  display: "flex",
                  flexDirection: "column",
                  textAlign: "justify",
                }}
              >
                <View style={{ ...styles.section, marginTop: 20 }}>
                  <Text
                    style={{
                      ...styles.bodyText,
                      lineHeight: 1.2,
                      textAlign: "justify",
                    }}
                  >
                    {note ? (
                      <>
                        <Text
                          style={{
                            ...styles.bodyTextBold,
                            lineHeight: 1.2,
                            textAlign: "justify",
                            fontFamily: "Helvetica-Bold",
                          }}
                        >
                          Note added from office:
                        </Text>{" "}
                        {note}{" "}
                      </>
                    ) : (
                      ""
                    )}
                  </Text>
                </View>
              </View>
            )}
            {customPdfContent?.showDisclaimer && (
              <V3Disclaimer
                hoursEstimated={hoursEstimated}
                variant={
                  customPdfContent?.disclaimerType === "firstSentence"
                    ? "firstSentence"
                    : "full"
                }
              />
            )}
            {!customPdfContent?.showTermsAndConditions && (
              <View
                style={{
                  marginTop: 40,
                  borderTop: "1px solid black",
                  width: "50%",
                  alignSelf: "center",
                }}
              >
                <Text
                  style={{
                    ...styles.bodyText,
                    textAlign: "center",
                    marginTop: 4,
                  }}
                >
                  Sign here
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* END OF CONTENT */}
        <Footer facilityName={facilityName} />
      </Page>
      {customPdfContent?.showTermsAndConditions && (
        <PlasticSurgeryText
          facilityLogo={facilityLogo}
          fullAddress={fullAddress || ""}
          phoneNumber={phoneNumber || ""}
          facilityName={facilityName || ""}
          showAddress={false}
          customTermsAndConditions={customPdfContent?.termsAndConditionsText}
        />
      )}
    </Document>
  );
}
